import { z } from 'zod'
import { note300, uuid } from './primitives.ts'

/** M16-J: the measured cutting setup (GK-013, the setup-history half of GK-012; docs/M16.md J). The height a mechanic measured on a cutting unit or on a
 *  machine, with the method and the moment, beside the job's own target when he picked one (copied, never computed). The schema mirrors
 *  `core.command_type.payload_schema` in `20261201100900_cutting_setup.sql`; the contract check (ops/contracts/check.mjs) runs the same fixtures and random
 *  payloads through both.
 *  - `cutting_setup.record@1` is a FACT of the field (a new permission `setup.record`: the mechanic and the crew by name, the direction by the generic rule;
 *    offline-first, projected in `sync/projectors.cutting.ts`; kiosk-allowed by rule 9). A height is a positive number of millimetres with at most two
 *    decimals (the column is numeric(5,2) and keeps it as entered: the handler refuses a third decimal rather than round it); the method is one of four
 *    words, a FIELD of the record and never a question asked of the club. */
export const CUTTING_METHODS = ['gauge', 'prism', 'bench', 'other'] as const
export type CuttingMethod = (typeof CUTTING_METHODS)[number]
/** The bounds of a height in millimetres (numeric(5,2): above zero, under 1000). */
export const CUTTING_MM_MAX = 999.99
/** @TS: an instant as the device writes it (toISOString), the pattern the stored schema uses (the same as the notebook's and the observations' facts). */
const instant = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9:.]+(Z|[+-][0-9]{2}:?[0-9]{2})$/)
const mm = z.number().gt(0).max(CUTTING_MM_MAX)

export const cuttingSetupRecordV1 = z.strictObject({
  setup_id: uuid, asset_id: uuid, measured_mm: mm, target_mm: mm.nullable().optional(), method: z.enum(CUTTING_METHODS),
  measured_at: instant.optional(), note: note300.optional(), job_id: uuid.optional(),
})

export type CuttingSetupRecordV1 = z.infer<typeof cuttingSetupRecordV1>
